import { useCallback, useMemo } from 'react';
import { useTranslation } from '../../../i18n/LanguageContext';
import { getCarrierLabel, parseShipmentCost } from '../../../utils/shipments';
import { getCargoDirection, getCargoStatus } from '../../../utils/companyShipments';

// Firma adları özel isim; yalnızca "Kurye" ve "Diğer" çevrilir
const TRANSLATED_CARRIERS = ['kurye', 'diger'];

/**
 * Kargo ekranlarının metinleri. Kayıttaki değerler (yön, durum, firma kodu)
 * aynı kalır; etiketler seçili dile göre değere bakılarak bulunur.
 */
const useCargoText = () => {
  const { t, language, locale } = useTranslation();

  const label = useCallback((key, vars) => t(`cargo.${key}`, vars), [t]);

  return useMemo(() => {
    const directionText = (value) => {
      const key = getCargoDirection(value).value;
      return {
        value: key,
        label: label(`direction.${key}.label`),
        short: label(`direction.${key}.short`),
        description: label(`direction.${key}.description`),
        party: label(`direction.${key}.party`),
        partyName: label(`direction.${key}.partyName`),
        address: label(`direction.${key}.address`),
      };
    };

    const statusText = (direction, status) => {
      const option = getCargoStatus(direction, status);
      const key = getCargoDirection(direction).value;
      return { ...option, label: label(`status.${key}.${option.value}`) };
    };

    const carrierName = (value) =>
      TRANSLATED_CARRIERS.includes(value) ? label(`carriers.${value}`) : getCarrierLabel(value);

    // 'yyyy-mm-dd' → '12 Mart 2026' / '12 March 2026'
    const formatDate = (value) => {
      if (!value) return null;
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return null;
      return date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
    };

    const formatDateTime = (value) => {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleString(locale, { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
    };

    const formatCost = (value) => {
      const amount = parseShipmentCost(value);
      if (amount === null || Number.isNaN(amount)) return null;
      return amount.toLocaleString(locale, { style: 'currency', currency: 'TRY' });
    };

    return { t, language, locale, label, directionText, statusText, carrierName, formatDate, formatDateTime, formatCost };
  }, [t, language, locale, label]);
};

export default useCargoText;
